import React from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { Delete } from 'app/components/commons/ExtraButtons/Delete/Delete';
import type { Mode } from 'app/components/commons/Hotel/Types';

type PageContext = {
  isCreatePage: boolean;
  roomId?: number;
};

type FormState = {
  mode: Mode;
  canSubmit: boolean;
  isLoading: boolean;
};

type Actions = {
  onEdit?: () => void;
  onSave: () => void;
  onCancel?: () => void;
  onDelete: () => void;
};

type DisplayConfig = {
  title?: React.ReactNode;
  buttonSaveText?: string;
  showDelete?: boolean;
};

type RoomDetailHeaderProps = PageContext &
  FormState &
  Actions &
  DisplayConfig & {
    children: React.ReactNode;
  };

const useHeaderLogic = ({
  isCreatePage,
  showDelete,
  roomId,
}: PageContext & Pick<DisplayConfig, 'showDelete'>) => {
  const shouldShowEdit = !isCreatePage;
  const shouldShowCancel = !isCreatePage;
  const shouldShowDelete = !isCreatePage && showDelete;

  return {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription: `Room #${roomId}`,
  };
};

export const RoomDetailHeader = ({
  isCreatePage,
  roomId,
  mode,
  canSubmit,
  isLoading,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  title,
  buttonSaveText,
  showDelete = true,
  children,
}: RoomDetailHeaderProps) => {
  const {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription,
  } = useHeaderLogic({
    isCreatePage,
    roomId,
    showDelete,
  });

  return (
    <SDetailLayout
      title={title}
      mode={mode}
      onEdit={shouldShowEdit ? onEdit : undefined}
      onSave={onSave}
      onCancel={shouldShowCancel ? onCancel : undefined}
      buttonSaveText={buttonSaveText}
      headerExtra={
        shouldShowDelete && (
          <Delete
            entityDescription={deleteEntityDescription}
            onDelete={onDelete}
          />
        )
      }
      isDirty
      backRoute="/rooms"
      isValid={canSubmit}
      saveDisabled={!canSubmit}
      isLoading={!canSubmit && isLoading}
    >
      {children}
    </SDetailLayout>
  );
};
